import {
  VTextField,
  VAutocomplete,
  VTextarea,
  VSelect
} from 'vuetify/components';

import DynamicJsonField from '@/components/form/DynamicJsonField.vue';
import GroupedFieldsExpansionPanel from '@/components/form/GroupedFieldsExpansionPanel.vue';
import DatePickerInput from '@/components/form/DatePickerInput.vue';
import FileUploader from '@/components/form/FileUploader.vue';

import type { FormField } from '@/types/form';

const addressFields: FormField[] = [
  { value: 'street', label: 'Calle', component: VTextField, col: 6 },
  { value: 'exterior', label: '# Exterior', component: VTextField, col: 6 },
  { value: 'interior', label: '# Interior', component: VTextField, col: 6 },
  { value: 'neighborhood', label: 'Colonia', component: VTextField, col: 6 },
  { value: 'zip', label: 'Código Postal', component: VTextField, col: 6 },
  { value: 'city', label: 'Ciudad', component: VTextField, col: 6 },
  { value: 'municipality', label: 'Municipio', component: VTextField, col: 6 },
  { value: 'state', label: 'Estado', component: VTextField, col: 6 },
  { value: 'country', label: 'País', component: VTextField, col: 6 },
];

const socialPlatformFields = (prefix: string): FormField => ({
  value: `${prefix}_panel`,
  label: prefix.charAt(0).toUpperCase() + prefix.slice(1),
  component: GroupedFieldsExpansionPanel,
  col: 12,
  children: [
    { value: `${prefix}_account`, label: 'Cuenta', component: VTextField, col: 6 },
    { value: `${prefix}_avg_likes`, label: 'Avg Likes', component: VTextField, type: 'number', col: 6 },
    { value: `${prefix}_avg_comments`, label: 'Avg Comments', component: VTextField, type: 'number', col: 6 },
    { value: `${prefix}_pct_male`, label: '% Seguidores', component: VTextField, type: 'number', col: 3 },
    { value: `${prefix}_pct_female`, label: '% Seguidoras', component: VTextField, type: 'number', col: 3 },
    {
      value: `${prefix}_audience`,
      label: 'Edad público',
      isJsonArray: true,
      component: DynamicJsonField,
      col: 12,
      children: [
        { value: 'label', label: 'Rango', component: VTextField, col: 6 },
        { value: 'value', label: 'Porcentaje', component: VTextField, type: 'number', col: 6 },
      ],
      transform: (items: any[]) => items
    },
  ],
});

export const CREATE_FORM: FormField[] = [
  { value: 'name', label: 'Nombre', component: VTextField, required: false, col: 6, mdcol:12 },
  {
    value: 'user',
    label: 'TA',
    component: VAutocomplete,
    searchValue: 'name',
    fetchOptions: 'searchUsers',
    itemText: (item: any) => [item?.name, item?.last_name].filter(Boolean).join(' ') || '',
    itemValue: (item: any) => item,
    required: false,
    col: 6,
    fetchParams: () => ({
      'filter[is_active]': 1
    }),
    transform: (value: any) => ({
      user_id: value?.id ?? value,
    })
  },
  { value: 'email', label: 'Email', component: VTextField, required: false, col: 12, mdcol:12 },
  {
    value: 'department',
    label: 'Departamento',
    component: VAutocomplete,
    fetchOptions: 'searchOrganizations',
    itemText: (item: any) => item?.legal_name || item?.name || '',
    itemValue: (item: any) => item,
    col: 6,
    transform: (value: any) => ({
      department_id: value?.id ?? value,
    })
  },
  { value: 'categories', 
    label: 'Categorias', 
    component: VSelect, 
    searchValue: 'name', 
    required: false, 
    col: 6, 
    fetchOptions: 'influencerCategoriesFromStore', 
    itemText: 'name', 
    itemValue: 'name',
    multiple:true
  },
  {
    value: 'phone',
    label: 'Teléfono',
    component: VTextField,
    col: 6,
  },
  {
    value: 'birthday',
    label: 'Cumpleaños',
    component: DatePickerInput,
    col: 6,
  },
  {
    value: 'address',
    label: 'Dirección',
    isJsonArray: true,
    plainJson: true,
    component: GroupedFieldsExpansionPanel,
    col: 12,
    children: addressFields,
  },
  {
    value: 'social_media',
    label: 'Social media',
    isJsonArray: true,
    plainJson: false,
    component: GroupedFieldsExpansionPanel,
    col: 12,
    children: [
      socialPlatformFields('instagram'),
      socialPlatformFields('tiktok'),
    ],
  },
  
  {
    value: 'payment_data',
    label: 'Datos de pago',
    isJsonArray: true,
    plainJson: true,
    component: GroupedFieldsExpansionPanel,
    col: 12,
    children: [
      { value: 'bank', label: 'Banco', component: VTextField, col: 6 },
      { value: 'account_number', label: 'Número de cuenta', component: VTextField, col: 6 },
      { value: 'clabe', label: 'CLABE', component: VTextField, col: 6 },
      { value: 'card', label: 'Tarjeta', component: VTextField, col: 6 },
      { value: 'commission_pct', label: '% Comisión', component: VTextField, type: 'number', col: 6 },
    ],
  },
  {
    value: 'fiscal_data',
    label: 'Datos fiscales',
    isJsonArray: true,
    plainJson: true,
    component: GroupedFieldsExpansionPanel,
    col: 12,
    children: [
      { value: 'tax_system', label: 'Régimen Fiscal', component: VAutocomplete, searchValue: 'name', required: false, col: 6, fetchOptions: 'searchFiscalRegiments', itemText: 'label',
        itemValue: (item: any) => item,
        transform: (value: any) => ({
          tax_system: value?.value,
        })
      },
      { value: 'rfc', label: 'RFC', component: VTextField, col: 6 },
    ],
  },
  {
    value: 'documents_panel',
    label: 'Documentos',
    isJsonArray: true,
    plainJson: true,
    component: GroupedFieldsExpansionPanel,
    layoutOnly: true,
    col: 12,
    children: [{
      value: 'documents',
      label: 'Documentos',
      isJsonArray: true,
      component: DynamicJsonField,
      col: 12,
      children: [
        { value: 'label', label: 'Etiqueta', component: VTextField, col: 6 },
        {
            value: 'file',
            label: 'Archivo',
            component: FileUploader,
            module: 'clients',
            col: 6,
            required: false,
          }
      ],
    }]
  },
  {
    value: 'sizes_panel',
    label: 'Tallas',
    isJsonArray: true,
    plainJson: true,
    component: GroupedFieldsExpansionPanel,
    layoutOnly: true,
    col: 12,
    children: [{
      value: 'sizes',
      label: 'Tallas',
      isJsonArray: true,
      component: DynamicJsonField,
      col: 12,
      children: [
        { value: 'label', label: 'Etiqueta', component: VTextField, col: 6 },
        { value: 'value', label: 'Valor', component: VTextField, col: 6 },
      ],
    }]
  }
];

export const EDIT_FORM: FormField[] = CREATE_FORM;

export const HEADERS = [
  { title: 'ID', value: 'id', format: 'text', sortable: true },
  { title: 'TA', value: 'user.name', format: 'text', sortable: false },
  { title: 'Departamento', value: 'department.name', format: 'text', sortable: false },
  { title: 'Teléfono', value: 'phone', format: 'text', sortable: false },
  { title: 'Cumpleaños', value: 'birthday', format: 'text', sortable: true },
  { title: 'RFC', value: 'fiscal_data.rfc', format: 'text', sortable: false },
  { title: 'Categorías', value: 'categories', format: 'text', sortable: false },
];

export const DEFAULT_HEADER_ORDER = ['id', 'user.name', 'department.name', 'phone', 'birthday'];

export const ROW_ACTIONS = [
  { label: 'Editar', onClick: '__edit', permission:'influencers.update' },
  { label: 'Eliminar', onClick: '__delete', permission:'influencers.delete' },
];

export const FILTERS = [
  { value: 'id', label: 'ID', component: VTextField },
  { value: 'name', label: 'Nombre', component: VTextField },
  { value: 'phone', label: 'Teléfono', component: VTextField },
  { value: 'department_id', label: 'Departamento', component: VAutocomplete, fetchOptions: 'searchOrganizations', itemText: 'legal_name', itemValue: (item: any) => item },
];

export const DETAIL = {
  expand_table: false,
  eye_detail: false,
  key: 'all',
};
